import { useEffect, useState } from "react";
import { ChevronDownIcon, UserRoundXIcon } from "lucide-react";

import { CandidacyRejectForm, Column, KeyboardShortcut, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { useInvalidateNotifications } from "@/hooks/invalidate/useInvalidateNotifications";
import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useUpdatePlacement } from "@/hooks/mutations/useUpdatePlacement";
import { useGetPlacements } from "@/hooks/queries/useGetPlacements";
import { useAddCandidacyStatusChange } from "@/hooks/sets/useAddCandidacyStatusChange";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { useSetPlacement } from "@/hooks/sets/useSetPlacement";
import { useSetRequisition } from "@/hooks/sets/useSetRequisition";
import {
  candidacyRejectionReasonToPlacementStatus,
  candidacyRejectionReasonToSubtitle,
  candidacyRejectionReasonToTitle,
  candidacyStatusToRejectionReasonGroup,
  defaultPlacementQueryParams,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { mutations } from "@/lib/mutations";
import { cn, isUserTyping } from "@/lib/utils";
import {
  CandidacyInfoResponse,
  CandidacyRejectionReason,
  CandidacyRejectionReasonGroup,
  CandidacyStatus,
  PlacementStatus,
} from "@/types";

const nonRejectableStatuses: CandidacyStatus[] = [
  CandidacyStatus.REJECTED,
  CandidacyStatus.EMPLOYED,
  CandidacyStatus.EMPLOYMENT_ENDED,
];

const buildStatusChangeReason = (
  reason: CandidacyRejectionReason,
  note?: string,
) => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const body = note?.trim() || candidacyRejectionReasonToSubtitle[reason];
  return `<p><strong>${candidacyRejectionReasonToTitle[reason]}</strong></p><p>${body}</p>`;
};

export const CandidacyRejectAction = ({
  buttonClassName,
  candidacyInfo,
  deferDashboardInvalidate = false,
  displayVariant = "compact",
  mixpanelScreen = MIXPANEL_SCREENS.CANDIDATE_SHEET,
  onRejected,
}: {
  buttonClassName?: string;
  candidacyInfo: CandidacyInfoResponse;
  // The review portal batches one dashboard refetch when it closes.
  deferDashboardInvalidate?: boolean;
  displayVariant?: "bottomSheet" | "compact" | "review";
  mixpanelScreen?: MIXPANEL_SCREENS;
  // Lets a host react to the rejection (the review queue advances on it).
  onRejected?: (payload: { status: CandidacyStatus }) => void;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: allPlacements } = useGetPlacements({
    jobId: candidacyInfo.jobId,
    queryOptions: defaultPlacementQueryParams,
  });

  // Update
  const { mutate: updateCandidacy, isPending } = useUpdateCandidacy();
  const { mutate: updatePlacement } = useUpdatePlacement();

  /* Cache Updates */
  // Add
  const addCandidacyStatusChange = useAddCandidacyStatusChange();

  // Set
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();
  const setPlacement = useSetPlacement();
  const setRequisition = useSetRequisition();
  const invalidateNotifications = useInvalidateNotifications();

  /* React Hooks */
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  /* Helpers */
  const isRejectable = !nonRejectableStatuses.includes(candidacyInfo.status);
  const isUnsuccessfulOffer =
    candidacyInfo.status === CandidacyStatus.OFFER ||
    candidacyInfo.status === CandidacyStatus.PLACED;
  const actionLabel = isUnsuccessfulOffer ? "Unsuccessful Offer" : "Reject";
  const placements =
    allPlacements?.filter((p) => p.candidacy.id === candidacyInfo.id) ?? [];

  // R -> open the reject dropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || e.metaKey) return;
      if (isUserTyping() || isDropdownOpen) return;
      if (e.key !== "r" || !isRejectable || isPending) return;

      e.preventDefault();
      mixpanelUtils.trackKeyboardShortcutUsed("R (Reject)", mixpanelScreen);
      setIsDropdownOpen(true);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDropdownOpen, isPending, isRejectable, mixpanelScreen]);

  /* Handlers */
  const handleConfirm = ({
    date,
    note,
    reason,
  }: {
    date?: string;
    note?: string;
    reason: CandidacyRejectionReason;
  }) => {
    if (isPending) return;

    const fromStatus = candidacyInfo.status;
    const toStatus =
      reason === CandidacyRejectionReason.COULD_NOT_CONTACT
        ? CandidacyStatus.COULD_NOT_CONTACT
        : CandidacyStatus.REJECTED;
    const statusChangeReason = buildStatusChangeReason(reason, note);
    const lastCandidacyStatusChange = {
      createdAt: new Date().toISOString(),
      fromStatus,
      toStatus,
      statusChangeReason,
      byAgent: false,
    };
    const placementStatus =
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      candidacyRejectionReasonToPlacementStatus[reason] ||
      (candidacyStatusToRejectionReasonGroup[candidacyInfo.status] ===
      CandidacyRejectionReasonGroup.POST_OFFER
        ? PlacementStatus.REJECTED
        : undefined);

    mixpanelUtils.trackButtonClick("Reject Candidate", mixpanelScreen, {
      from_status: fromStatus,
      rejected_reason: reason.toLowerCase(),
    });

    updateCandidacy(
      {
        candidacyId: candidacyInfo.id,
        updateCandidacyRequest: {
          fromStatus,
          status: toStatus,
          statusChangeReason,
          rejectedReason: reason,
        },
      },
      {
        onSuccess: () => {
          setCandidacyInAllLists({
            candidacyId: candidacyInfo.id,
            deferDashboardInvalidate,
            lastCandidacyStatusChange,
          });
          setCandidacyInfo({
            candidacyId: candidacyInfo.id,
            status: toStatus,
            lastStatusChangeByAgent: false,
            lastCandidacyStatusChange: {
              byAgent: lastCandidacyStatusChange.byAgent,
              fromStatus: lastCandidacyStatusChange.fromStatus,
              statusChangeReason: lastCandidacyStatusChange.statusChangeReason,
              toStatus: lastCandidacyStatusChange.toStatus,
            },
          });
          addCandidacyStatusChange(lastCandidacyStatusChange);

          // Rejecting out of APPLIED clears the "excellent candidate"
          // notification on the backend; refetch so the bell badge drops now.
          invalidateNotifications();

          if (placementStatus && placements.length > 0) {
            mutations.updatePlacement({
              placementId: placements[0].id,
              setPlacement,
              setRequisition,
              updatePlacement,
              updatePlacementRequest: {
                dateRejected:
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  dates.convertStringDateToIso(date || null) ??
                  new Date().toISOString(),
                status: placementStatus,
              },
            });
          }

          onRejected?.({ status: toStatus });
        },
      },
    );
    setIsDropdownOpen(false);
  };

  /* Render */
  const renderTrigger = () => {
    if (displayVariant === "bottomSheet") {
      return (
        <Button
          className={cn(
            "h-12 gap-2 rounded-full border-red-500 bg-red-50 px-4 text-sm font-semibold text-red-600 hover:border-red-600 hover:bg-red-100 hover:text-red-700",
            buttonClassName,
          )}
          disabled={!isRejectable || isPending}
          onClick={() => {
            setIsDropdownOpen(true);
          }}
          type="button"
          variant="outlineDestructive"
        >
          {actionLabel}
        </Button>
      );
    }

    if (displayVariant === "review") {
      return (
        <Button
          className={cn(
            "h-9 gap-2 rounded-full border-red-500 bg-red-50 px-4 text-sm font-semibold text-red-600 hover:border-red-600 hover:bg-red-100 hover:text-red-700",
            buttonClassName,
          )}
          disabled={!isRejectable || isPending}
          size="sm"
          variant="outlineDestructive"
        >
          {actionLabel}
          <KeyboardShortcut text="R" variant="red" />
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      );
    }

    return (
      <Button
        className={cn(
          "group/reject h-8 w-8 gap-0 overflow-hidden rounded-full px-0 text-slate-500 transition-[width,gap,padding] duration-200 hover:gap-2 hover:bg-red-50 hover:px-3 hover:text-red-700 data-[state=open]:gap-2 data-[state=open]:bg-red-50 data-[state=open]:px-3 data-[state=open]:text-red-700",
          isUnsuccessfulOffer
            ? "hover:w-44 data-[state=open]:w-44"
            : "hover:w-28 data-[state=open]:w-28",
          buttonClassName,
        )}
        disabled={!isRejectable || isPending}
        size="icon"
        variant="ghost"
      >
        <UserRoundXIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        <Row className="max-w-0 items-center gap-2 overflow-hidden whitespace-nowrap opacity-0 transition-[max-width,opacity] duration-200 group-hover/reject:max-w-36 group-hover/reject:opacity-100 group-data-[state=open]/reject:max-w-36 group-data-[state=open]/reject:opacity-100">
          <Text className="text-xs font-medium text-inherit">
            {actionLabel}
          </Text>
          <KeyboardShortcut text="R" variant="red" />
        </Row>
      </Button>
    );
  };

  const renderRejectForm = () => (
    <CandidacyRejectForm
      key={`reject-${candidacyInfo.id}`}
      mixpanelScreen={mixpanelScreen}
      onConfirm={handleConfirm}
      reasonGroup={candidacyStatusToRejectionReasonGroup[candidacyInfo.status]}
    />
  );

  const render = () => {
    if (displayVariant === "bottomSheet") {
      return (
        <>
          {renderTrigger()}
          <Sheet open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <SheetContent
              className="flex max-h-[85dvh] flex-col gap-0 rounded-t-2xl px-4 pb-8 pt-4"
              hideClose
              side="bottom"
            >
              <SheetHeader className="mb-3 text-left">
                <SheetTitle className="text-base font-semibold text-slate-900">
                  {actionLabel}
                </SheetTitle>
              </SheetHeader>
              <Column className="min-h-0 flex-1 overflow-y-auto">
                {renderRejectForm()}
              </Column>
            </SheetContent>
          </Sheet>
        </>
      );
    }

    return (
      <DropdownMenu onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
        <DropdownMenuTrigger asChild>{renderTrigger()}</DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="flex max-h-[var(--radix-dropdown-menu-content-available-height)] w-[440px] flex-col p-3"
          collisionPadding={12}
        >
          {renderRejectForm()}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return render();
};
